import { createElement } from 'react';
import { createRoute, type AnyRoute } from '@tanstack/react-router';
import { abaValida, CAMINHO_DETALHE, CAMINHO_LISTA } from '@/features/tickets/navigation';
import { TicketDetailPage } from '@/features/tickets/TicketDetailPage';
import { TicketListPage } from '@/features/tickets/TicketListPage';

/**
 * Rotas das telas de chamado.
 *
 * Exportadas como **fábrica**, e não como objetos prontos, porque
 * `createRoute` exige a rota pai e o pai (`appRoute`, com o `AppShell` e a
 * guarda de sessão) mora em `app/router.tsx`, que pertence a outro dono.
 * Receber o pai por parâmetro deixa a integração ser uma linha lá — sem que
 * este módulo precise importar de dentro de `app/`, o que criaria dependência
 * circular entre a feature e o roteador.
 *
 * Integração esperada em `app/router.tsx`:
 *
 * ```ts
 * const { lista, detalhe } = criarRotasDeChamados(appRoute);
 * appRoute.addChildren([indexRoute, diagnosticsRoute, lista, detalhe]);
 * ```
 */
export function criarRotasDeChamados<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const lista = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_LISTA,
    /**
     * Declara `?q=` para a busca global da casca poder navegar para cá.
     * Sem isto o TanStack descarta o parâmetro e a busca chega vazia.
     */
    validateSearch: (entrada: Record<string, unknown>): { q?: string } => {
      const q = entrada['q'];
      return typeof q === 'string' && q.trim() !== '' ? { q: q.trim() } : {};
    },
    component: TicketListPage,
  });

  const detalhe = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_DETALHE,
    /**
     * Declara `?aba=` para que outras telas possam apontar para uma seção do
     * chamado — o calendário de mudanças manda o comitê direto para a ficha de
     * Change Enablement. Sem isto o TanStack descarta o parâmetro, e o link
     * abriria sempre na descrição.
     */
    validateSearch: (entrada: Record<string, unknown>): { aba?: string } => {
      const aba = abaValida(entrada['aba']);
      return aba ? { aba } : {};
    },
    // `createElement` em vez de JSX para este arquivo continuar sendo `.ts`.
    // A conversão do parâmetro é necessária enquanto a rota pai é genérica: o
    // roteador só consegue inferir `{ ticketId }` depois que a árvore estiver
    // registrada em `app/router.tsx`.
    component: () => {
      const { ticketId } = detalhe.useParams() as { ticketId: string };
      return createElement(TicketDetailPage, { ticketId });
    },
  });

  return { lista, detalhe };
}

export { CAMINHO_DETALHE, CAMINHO_LISTA };
